import { useEffect, useId, useState } from "react";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useToast } from "@/hooks/useToast";
import { processFormExtraction, getFormTypes } from "@/api/form_extraction";
import { useMunicipality } from "@/context/MunicipalityContext";
import { formatMunicipalityLabel } from "@/lib/municipalityLabel";
import { Loader2, Upload } from "lucide-react";

const ACCEPTED_TYPES = "image/jpeg,image/jpg,image/png,image/heic";

type FormExtractionAssistantProps = {
  /** When true, omits page header and uses workspace MunicipalitySwitcher selection. */
  embedded?: boolean;
};

export function FormExtractionAssistant({ embedded = false }: FormExtractionAssistantProps) {
  const fileInputId = useId();
  const [submitting, setSubmitting] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [formTypes, setFormTypes] = useState<string[]>([]);
  const [selectedFormType, setSelectedFormType] = useState<string>("free_form");
  const [result, setResult] = useState<{
    extracted: Record<string, unknown>;
    usage?: { num_pages?: number; num_fields?: number };
    id?: number;
  } | null>(null);
  const { toast } = useToast();
  const { municipalities, municipality, setMunicipalityId } = useMunicipality();

  useEffect(() => {
    getFormTypes()
      .then((res) => {
        setFormTypes(res.form_types || []);
        if (res.form_types?.length && !res.form_types.includes(selectedFormType)) {
          setSelectedFormType(res.form_types[0]);
        }
      })
      .catch(() => setFormTypes(["free_form", "order_of_probation"]));
  }, []);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setError(null);
    setResult(null);

    if (!selectedFile) {
      setError("Please select an image file before submitting.");
      return;
    }

    try {
      setSubmitting(true);
      const data = await processFormExtraction({
        file: selectedFile,
        formType: selectedFormType,
        municipalityId: municipality?.id,
      });
      setResult({
        extracted: data.extracted || {},
        usage: data.usage,
        id: data.id,
      });
      toast({
        title: "Extraction complete",
        description: `Extracted ${Object.keys(data.extracted || {}).length} fields.`,
      });
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : "Extraction failed. Please try again.";
      setError(message);
      toast({
        title: "Extraction failed",
        description: message,
        variant: "destructive",
      });
    } finally {
      setSubmitting(false);
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setError(null);
    setResult(null);
    const file = e.target.files?.[0] || null;

    if (!file) {
      setSelectedFile(null);
      return;
    }

    const isImage =
      ACCEPTED_TYPES.split(",").some((t) => file.type === t.trim()) ||
      /\.(jpe?g|png|heic)$/i.test(file.name);
    if (!isImage) {
      setSelectedFile(null);
      setError("Only JPEG, PNG, or HEIC images are supported.");
      e.currentTarget.value = "";
      return;
    }

    setSelectedFile(file);
  };

  const handleReset = () => {
    setSelectedFile(null);
    setError(null);
    setResult(null);
    const input = document.getElementById(fileInputId) as HTMLInputElement;
    if (input) input.value = "";
  };

  return (
    <div className="flex flex-col flex-1 min-h-0 overflow-hidden" data-testid="form-extraction-assistant">
      {!embedded ? (
        <div className="p-4 flex-shrink-0 border-b">
          <div className="flex items-center justify-between">
            <div className="space-y-1">
              <h1 className="text-2xl font-bold">Permit Assistant</h1>
              <p className="text-muted-foreground">
                Upload a permit or other structured form to extract fields with OCR.
              </p>
            </div>
            {result && (
              <Button variant="outline" onClick={handleReset}>
                New extraction
              </Button>
            )}
          </div>
        </div>
      ) : null}

      <div className={embedded ? "flex-1 overflow-auto p-4" : "flex-1 p-4 overflow-auto"}>
        {embedded && result ? (
          <div className="mb-4 flex justify-end">
            <Button variant="outline" onClick={handleReset}>
              New extraction
            </Button>
          </div>
        ) : null}
        {submitting ? (
          <Card className="max-w-2xl p-8">
            <div className="flex flex-col items-center justify-center gap-4 text-center">
              <Loader2 className="h-12 w-12 text-primary animate-spin" />
              <div className="space-y-1">
                <p className="font-medium">Extracting form data...</p>
                <p className="text-sm text-muted-foreground">
                  OCR processing may take 30–90 seconds. Please wait.
                </p>
              </div>
            </div>
          </Card>
        ) : (
        <Card className="max-w-2xl p-4 space-y-4">
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <label className="text-lg font-semibold" htmlFor={fileInputId}>
                Upload form image
              </label>
              <Input
                type="file"
                accept={ACCEPTED_TYPES}
                id={fileInputId}
                onChange={handleFileChange}
              />
              <p className="text-xs text-muted-foreground">
                Accepted formats: JPEG, PNG, HEIC (max 20MB)
              </p>
              {error && (
                <p className="text-xs text-destructive" role="alert">
                  {error}
                </p>
              )}
            </div>

            <div className="space-y-2">
              <label className="text-lg font-semibold" htmlFor="form-type-select">
                Document type
              </label>
              <select
                id="form-type-select"
                className="w-full border rounded px-3 py-2 text-sm bg-background"
                value={selectedFormType}
                onChange={(e) => setSelectedFormType(e.target.value)}
              >
                {formTypes.map((ft) => (
                  <option key={ft} value={ft}>
                    {ft === "free_form"
                      ? "Free form (any document – AI extracts all fields)"
                      : ft.replace(/_/g, " ")}
                  </option>
                ))}
              </select>
              {selectedFormType === "free_form" && (
                <p className="text-xs text-muted-foreground">
                  Use for documents without a predefined schema. The AI will detect and extract all
                  fields it finds.
                </p>
              )}
            </div>

            {!embedded && municipalities.length > 0 && (
              <div className="space-y-2">
                <label className="text-lg font-semibold" htmlFor="municipality-select">
                  Municipality (optional)
                </label>
                <Select
                  value={municipality?.id ? String(municipality.id) : "0"}
                  onValueChange={(value) => setMunicipalityId(Number(value) || 0)}
                >
                  <SelectTrigger id="municipality-select">
                    <SelectValue placeholder="None" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="0">None</SelectItem>
                    {municipalities.map((m) => (
                      <SelectItem key={m.id} value={String(m.id)}>
                        {formatMunicipalityLabel(m)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            <div className="flex items-center gap-2">
              <Button type="submit">
                <Upload className="mr-2 h-4 w-4" />
                Extract
              </Button>
            </div>
          </form>
        </Card>
        )}

        {result && (
          <Card className="mt-4 p-4">
            <h2 className="text-lg font-semibold mb-2">Extracted data</h2>
            {result.usage && (
              <p className="text-xs text-muted-foreground mb-2">
                {result.usage.num_pages} page(s), {result.usage.num_fields} fields
                {result.id && ` • Record #${result.id}`}
              </p>
            )}
            <pre className="text-sm bg-muted p-4 rounded-md overflow-auto max-h-96">
              {JSON.stringify(result.extracted, null, 2)}
            </pre>
          </Card>
        )}
      </div>
    </div>
  );
}
